import React, { createContext, FC, memo, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import mapValues from 'lodash/mapValues';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { IESSAvailabilityCycleDTO, IUpsertAvailabilityPreferencesRequestDTO } from 'src/api/dtos';
import AvailabilityRepository from 'src/api/repositories/ess/v1/availability.repository';
import { IError } from 'src/api/types';
import {
  AvailabilityCycleInputsType,
  EditedPeriodDateType,
  PreferenceInputsType,
} from 'src/features/availabilities/availabilities.types';
import { buildEditCycleInputs, getAvailabilityDateText } from 'src/features/availabilities/availabilities.utils';
import { Modal } from 'src/shared/components';
import { AvailabilityStatusEnums, CycleTypeEnums, OperationTypeEnums } from 'src/shared/enums';
import { usePrevious } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { getErrorMessage, getSecondsFromTime } from 'src/shared/utils';

const uninitializedCycle: IESSAvailabilityCycleDTO = {
  id: -1,
  employeeId: -1,
  cycleStart: '',
  validFrom: '',
  cycleLength: -1,
  type: CycleTypeEnums.WEEKLY,
  preferences: [],
  isLocked: false,
};

type EmployeeAvailabilityRecurringCycleProps = {
  inputs: AvailabilityCycleInputsType | undefined;
  editedPeriodDate: EditedPeriodDateType | undefined;
  touched: FormikTouched<AvailabilityCycleInputsType>;
  errors: FormikErrors<AvailabilityCycleInputsType>;
  loading: boolean;
  updating: boolean;
  apiError: string;
  onEditedChange: (periodKey: string, dateKey: string) => void;
  onChangeFullDay: (wasAllDay: boolean, periodKey: string, dateKey: string) => void;
  onChangeStatus: (
    status: AvailabilityStatusEnums,
    periodKey: string,
    dateKey: string,
    preferenceIndex: number,
  ) => void;
  onDelete: (periodKey: string, dateKey: string, preferenceIndex: number) => void;
  onAdd: (periodKey: string, dateKey: string) => void;
  onChangeStartTime: (value: string | undefined, periodKey: string, dateKey: string, preferenceIndex: number) => void;
  onChangeEndTime: (value: string | undefined, periodKey: string, dateKey: string, preferenceIndex: number) => void;
  onCancel: () => void;
  onSave: () => void;
  onDeleteDate: (periodKey: string, dateKey: string) => void;
};

const EmployeeAvailabilityRecurringCycleContext = createContext<EmployeeAvailabilityRecurringCycleProps>(
  {} as EmployeeAvailabilityRecurringCycleProps,
);

export const useEmployeeAvailabilityRecurringCycle = (): EmployeeAvailabilityRecurringCycleProps =>
  useContext<EmployeeAvailabilityRecurringCycleProps>(EmployeeAvailabilityRecurringCycleContext);

export const EmployeeAvailabilityRecurringCycleProvider: FC<{ children: ReactNode }> = memo(({ children }) => {
  const { t } = useTranslate();

  const [editedPeriodDate, setEditedPeriodDate] = useState<EditedPeriodDateType>();
  const [apiError, setApiError] = useState<string>('');

  const { data: currentCycle, isLoading, isFetching } = AvailabilityRepository.endpoints.useGetCurrentCycleQuery();

  const [updatePreferences, { isLoading: isUpdating }] =
    AvailabilityRepository.endpoints.useUpdatePreferencesMutation();

  const handleUpsertPreferences = async () => {
    if (!editedPeriodDate || !inputs) {
      return;
    }

    const { periodKey, dateKey } = editedPeriodDate;

    const preferences = inputs.groups[periodKey]?.[dateKey];

    if (!preferences.length) {
      return;
    }

    const [sharedPreference] = preferences;

    const dayOfCycle = sharedPreference.dayOfCycle;

    const payload: IUpsertAvailabilityPreferencesRequestDTO = {
      cycleId: inputs.id,
      dayOfCycle,
      preferences: preferences.map(({ operationType, status, id, startTimeInSeconds, endTimeInSeconds }) => ({
        operationType,
        status,
        id,
        startTimeInSeconds,
        endTimeInSeconds,
      })),
    };

    try {
      await updatePreferences(payload).unwrap();

      toast.info(t('availability.youHaveSuccessfullyUpdatedYourPreferences'), { type: toast.TYPE.SUCCESS });
      setEditedPeriodDate(undefined);
      setApiError('');
    } catch (err) {
      const error = err as IError;
      console.error('Submit Error', { ...error });
      const errorMessage = getErrorMessage(error, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
      setApiError(errorMessage);
    }
  };

  Yup.setLocale({
    mixed: {
      required: t('global.requiredField'),
    },
  });

  const validationSchema = Yup.object().shape({
    // Yup lazy is used to validate groups as it's an object with dynamic keys
    groups: Yup.lazy((periods) =>
      Yup.object(
        mapValues(periods, () =>
          Yup.lazy((dates) =>
            Yup.object(
              mapValues(dates, () =>
                Yup.array().of(
                  Yup.object({
                    status: Yup.mixed<AvailabilityStatusEnums>()
                      .test((value) =>
                        Object.values(AvailabilityStatusEnums).includes(value as AvailabilityStatusEnums),
                      )
                      .required(),
                    operationType: Yup.mixed<OperationTypeEnums>()
                      .test((value) => Object.values(OperationTypeEnums).includes(value as OperationTypeEnums))
                      .required(),
                    id: Yup.number().optional(),
                    startTimeInSeconds: Yup.number().optional(),
                    endTimeInSeconds: Yup.number().optional(),
                  }),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  });

  const {
    values: inputs,
    setValues,
    setFieldValue,
    handleSubmit: handleSave,
    initialValues,
    touched,
    errors,
    resetForm,
  } = useFormik<AvailabilityCycleInputsType>({
    enableReinitialize: true,
    initialValues: buildEditCycleInputs(currentCycle ?? uninitializedCycle),
    validationSchema,
    onSubmit: handleUpsertPreferences,
  });

  const isInitial = isEqual(inputs, buildEditCycleInputs(uninitializedCycle));

  const loading = isLoading || isFetching || isInitial;

  const previousCurrentCycle = usePrevious(currentCycle);

  useEffect(() => {
    if (!currentCycle || isEqual(previousCurrentCycle, currentCycle)) {
      return;
    }

    setValues(buildEditCycleInputs(currentCycle));
    resetForm();
  }, [currentCycle]);

  const handleEditedChange = (periodKey: string, dateKey: string) => {
    resetForm();
    setApiError('');
    setEditedPeriodDate({ periodKey, dateKey });
  };

  const handleCancel = () => {
    setEditedPeriodDate(undefined);
    setApiError('');
    resetForm();
  };

  const handleChangeFullDay = (wasAllDay: boolean, periodKey: string, dateKey: string) => {
    const initialPreferences = initialValues.groups[periodKey][dateKey];

    const hasInitialAllDay = !initialPreferences.some(
      ({ startTimeInSeconds, endTimeInSeconds }) => !isNil(startTimeInSeconds) || !isNil(endTimeInSeconds),
    );

    if (wasAllDay) {
      setFieldValue(
        `groups[${periodKey}][${dateKey}]`,
        initialPreferences.map((preference) => ({
          ...preference,
          startTimeInSeconds: preference.startTimeInSeconds ?? 0,
          endTimeInSeconds: preference.endTimeInSeconds ?? 0,
        })),
      );
    } else {
      setFieldValue(
        `groups[${periodKey}][${dateKey}]`,
        hasInitialAllDay
          ? [...initialPreferences]
          : initialPreferences.map((preference, index) => ({
              ...preference,
              startTimeInSeconds: undefined,
              endTimeInSeconds: undefined,
              operationType: index === 0 ? OperationTypeEnums.UPDATE : OperationTypeEnums.DELETE,
            })),
      );
    }
  };

  const handleChangeStatus = (
    status: AvailabilityStatusEnums,
    periodKey: string,
    dateKey: string,
    preferenceIndex: number,
  ) => {
    setFieldValue(
      `groups[${periodKey}][${dateKey}][${preferenceIndex}].status`,
      status === AvailabilityStatusEnums.AVAILABLE
        ? AvailabilityStatusEnums.NOT_AVAILABLE
        : AvailabilityStatusEnums.AVAILABLE,
    );
  };

  const handleDelete = (periodKey: string, dateKey: string, preferenceIndex: number) => {
    const preferences = inputs.groups[periodKey][dateKey];

    // Do not allow deleting last override
    if (preferences.length <= 1) {
      return;
    }

    setFieldValue(
      `groups[${periodKey}][${dateKey}]`,
      preferences.reduce<PreferenceInputsType[]>((list, current, index) => {
        if (index !== preferenceIndex) {
          return [...list, { ...current }];
        }

        if (current.operationType === OperationTypeEnums.ADD) {
          return [...list];
        }

        return [...list, { ...current, operationType: OperationTypeEnums.DELETE }];
      }, []),
    );
  };

  const handleAdd = (periodKey: string, dateKey: string) => {
    const preferences = inputs.groups[periodKey][dateKey];

    const lastPreference = preferences[preferences.length - 1];

    setFieldValue(`groups[${periodKey}][${dateKey}]`, [
      ...preferences,
      {
        id: undefined,
        operationType: OperationTypeEnums.ADD,
        status: lastPreference.status,
        startTimeInSeconds: !isNil(lastPreference.startTimeInSeconds) ? 0 : undefined,
        endTimeInSeconds: !isNil(lastPreference.endTimeInSeconds) ? 0 : undefined,
      },
    ]);
  };

  const handleChangeStartTime = (
    value: string | undefined,
    periodKey: string,
    dateKey: string,
    preferenceIndex: number,
  ) => {
    setFieldValue(`groups[${periodKey}][${dateKey}][${preferenceIndex}].startTimeInSeconds`, getSecondsFromTime(value));
  };

  const handleChangeEndTime = (
    value: string | undefined,
    periodKey: string,
    dateKey: string,
    preferenceIndex: number,
  ) => {
    setFieldValue(`groups[${periodKey}][${dateKey}][${preferenceIndex}].endTimeInSeconds`, getSecondsFromTime(value));
  };

  const confirmDeleteDate = async (periodKey: string, dateKey: string) => {
    if (!inputs) {
      return;
    }

    const preferences = inputs.groups[periodKey]?.[dateKey];

    if (!preferences.length) {
      return;
    }

    const [sharedPreference] = preferences;

    const dayOfCycle = sharedPreference.dayOfCycle;

    const payload: IUpsertAvailabilityPreferencesRequestDTO = {
      cycleId: inputs.id,
      dayOfCycle,
      preferences: preferences
        .filter((preference) => !isNil(preference.id))
        .map(({ id, status }) => ({
          operationType: OperationTypeEnums.DELETE,
          id,
          status,
        })),
    };

    try {
      await updatePreferences(payload).unwrap();

      toast.info(t('availability.youHaveSuccessfullyDeletedYourPreferences'), { type: toast.TYPE.SUCCESS });
      setApiError('');
    } catch (err) {
      const error = err as IError;
      console.error('Submit Error', { ...error });
      const errorMessage = getErrorMessage(error, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
      setApiError(errorMessage);
    } finally {
      setOpenDeleteModal(false);
      setDeletedPeriodDate(undefined);
    }
  };

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deletedPeriodDate, setDeletedPeriodDate] = useState<EditedPeriodDateType>();

  const handleDeleteDate = (periodKey: string, dateKey: string) => {
    setDeletedPeriodDate({ periodKey, dateKey });
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteDateModal = () => {
    setOpenDeleteModal(false);
  };

  const handleSubmitDeleteDateModal = () => {
    if (!deletedPeriodDate) {
      return;
    }

    const { periodKey, dateKey } = deletedPeriodDate;

    confirmDeleteDate(periodKey, dateKey);
  };

  const deleteModalMessage = useMemo(() => {
    if (!deletedPeriodDate || !inputs) {
      return '';
    }

    const { periodKey, dateKey } = deletedPeriodDate;

    const [sharedPreference] = inputs.groups[periodKey][dateKey];

    if (!sharedPreference) {
      return '';
    }

    const periodIndex = Object.keys(inputs.groups).findIndex((key) => key === periodKey) + 1;

    return t(
      'availability.areYouSureYouWantToDeleteAllPreferencesFor',
      getAvailabilityDateText(inputs.type, periodIndex, sharedPreference.dayOfCycle, dateKey, t),
    );
  }, [deletedPeriodDate]);

  const value: EmployeeAvailabilityRecurringCycleProps = {
    inputs,
    editedPeriodDate,
    touched,
    errors,
    loading,
    updating: isUpdating,
    apiError,
    onEditedChange: handleEditedChange,
    onCancel: handleCancel,
    onChangeFullDay: handleChangeFullDay,
    onChangeStatus: handleChangeStatus,
    onDelete: handleDelete,
    onAdd: handleAdd,
    onChangeStartTime: handleChangeStartTime,
    onChangeEndTime: handleChangeEndTime,
    onSave: handleSave,
    onDeleteDate: handleDeleteDate,
  };

  return (
    <EmployeeAvailabilityRecurringCycleContext.Provider value={value}>
      {children}
      <Modal
        open={openDeleteModal}
        onClose={handleCloseDeleteDateModal}
        modalHeaderProps={{ title: 'Delete Preferences', onClose: handleCloseDeleteDateModal }}
        modalFooterProps={{
          submitText: t('global.delete'),
          onSubmit: handleSubmitDeleteDateModal,
          closeText: t('global.cancel'),
          onClose: handleCloseDeleteDateModal,
          loading: isUpdating,
          disabled: loading,
        }}
      >
        <Typography>{deleteModalMessage}</Typography>
      </Modal>
    </EmployeeAvailabilityRecurringCycleContext.Provider>
  );
});
